import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Map as MapIcon, Sparkles, Trophy, Shield, LogOut, User as UserIcon, Heart, Settings, Bell, BellRing, Bookmark, MessageSquare } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";
import { AssistantToggle } from "@/components/ContextAssistant";
import LanguageSelect from "@/components/LanguageSelect";
import { useMarketplaceUnread } from "@/lib/marketplaceUnread";
import { useChatUnread } from "@/lib/chatUnread";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const { total: mkUnread } = useMarketplaceUnread();
  const { total: chatUnread } = useChatUnread();
  const active = (p) => loc.pathname === p;

  const NavLink = ({ to, icon: Icon, label, testid, badge }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium hover:bg-emerald-50 hover:text-emerald-700 ${active(to) ? "text-emerald-700 bg-emerald-50" : "text-slate-700"}`}
    >
      <Icon size={16} />
      <span className="hidden md:inline">{label}</span>
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
          aria-label={`${badge} ${t("unread_messages")}`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );

  return (
    // Lotto 6 #6 — compact sticky header on mobile.
    // Vertical padding shrinks from py-3 (24px) to py-2 (16px) on <sm,
    // brand logo shrinks from h-9 to h-8, so the header occupies ~48px on
    // small phones instead of ~60px. On tablet+ nothing changes. Sticky was
    // already in place; the compaction gives the map ~12px more room and
    // keeps profile/notif/language visually anchored on every scroll.
    <header className="sticky top-0 z-40 w-full" data-testid="app-header">
      <div className="glass-panel border-b border-emerald-200/60">
        <div className="max-w-7xl mx-auto px-3 md:px-8 py-2 md:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <Link to="/" className="flex items-center group shrink-0" data-testid="home-link" aria-label="ToClean · World · In · Action">
            <BrandLogo className="block h-8 sm:h-10 max-w-full" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/map" icon={MapIcon} label={t("nav_map")} testid="nav-map" />
            <NavLink to="/marketplace" icon={Sparkles} label={t("nav_marketplace")} testid="nav-marketplace" badge={user ? mkUnread : 0} />
            <NavLink to="/events/history" icon={Bell} label={t("past_extraordinary_events_label")} testid="nav-events-history" />
            <NavLink to="/reforestation" icon={Sparkles} label={t("nav_reforestation")} testid="nav-reforestation" />
            <NavLink to="/leaderboard" icon={Trophy} label={t("nav_leaderboard")} testid="nav-leaderboard" />
            <NavLink to="/stories" icon={Sparkles} label={t("nav_stories")} testid="nav-stories" />
            {user && (user.role === "admin" || user.role === "moderator") && (
              <NavLink to="/admin" icon={Shield} label={t("nav_admin")} testid="nav-admin" />
            )}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 shrink min-w-0 flex-nowrap">
            <LanguageSelect testid="header-lang-select" />
            <div className="hidden sm:block"><AssistantToggle /></div>
            <Link to="/donate" data-testid="cta-donate" className="shrink-0">
              <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-800 hover:bg-emerald-50 hover:text-emerald-900 px-2 sm:px-3 h-8" aria-label={t("cta_donate")}>
                <Heart size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline text-xs sm:text-sm">{t("cta_donate")}</span>
              </Button>
            </Link>
            {user ? (
              <>
                <NotificationsBell audience={user.role === "admin" || user.role === "moderator" ? "admin" : "user"} testid="notif-bell" />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full p-1 hover:bg-slate-100 hover:text-slate-900">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-800 text-xs font-semibold">{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav(`/profile/${user.user_id}`)}><UserIcon size={14} className="mr-2" /> {t("nav_profile")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-my-listings" onClick={() => nav("/me/marketplace")}><Sparkles size={14} className="mr-2" /> {t("menu_my_listings")}{mkUnread > 0 && <span className="ml-auto text-[10px] font-bold text-white bg-red-600 rounded-full px-1.5 min-w-[16px] text-center">{mkUnread > 99 ? "99+" : mkUnread}</span>}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-following" onClick={() => nav("/me/following")}><BellRing size={14} className="mr-2" /> {t("menu_following")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-chats" onClick={() => nav("/chats")}><MessageSquare size={14} className="mr-2" /> {t("nav_messages")}{chatUnread > 0 && <span className="ml-auto text-[10px] font-bold text-white bg-red-600 rounded-full px-1.5 min-w-[16px] text-center">{chatUnread > 99 ? "99+" : chatUnread}</span>}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-settings" onClick={() => nav("/settings")}><Settings size={14} className="mr-2" /> {t("nav_settings")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-notifications" onClick={() => nav("/notifications")}><Bell size={14} className="mr-2" /> {t("notifications")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-leaderboard" onClick={() => nav("/leaderboard")}><Trophy size={14} className="mr-2" /> {t("classifications_title")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-saved-searches" onClick={() => nav("/marketplace?saved=1")}><Bookmark size={14} className="mr-2" /> {t("menu_saved_searches")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-map" onClick={() => nav("/map")}><MapIcon size={14} className="mr-2" /> {t("nav_map")}</DropdownMenuItem>
                  {(user.role === "admin" || user.role === "moderator") && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><Shield size={14} className="mr-2" /> {t("nav_admin_dashboard")}</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut size={14} className="mr-2" /> {t("logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/auth" data-testid="cta-signin" className="hidden sm:block"><Button variant="ghost" size="sm" className="rounded-full hover:bg-slate-100 hover:text-slate-900">{t("cta_signin")}</Button></Link>
                <Link to="/auth?mode=signup" data-testid="cta-signup"><Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white px-3 sm:px-4">{t("cta_signup")}</Button></Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
